import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "@lingui/core/macro";
import { i18n } from "@app/i18n";
import type { ClientSession } from "shared/features/auth";
import { apiClient } from "@/lib/ApiClient";

export const sessionKeys = {
  all: ["sessions"] as const,
};

/** 이 계정으로 로그인된 세션(기기/브라우저) 목록을 최근 활동 순으로 반환한다.
 *  better-auth 의 built-in `/list-sessions` 대신 커스텀 `/api/sessions` 를 호출해
 *  freshAge 와 무관하게 해당 유저의 모든 세션을 DB 에서 직접 조회한다. */
export function useListSessionsQuery() {
  return useQuery({
    queryKey: sessionKeys.all,
    queryFn: async () => {
      const res = await apiClient.api.sessions.$get();
      // 응답이 실패했는지
      const isResNotOk = !res.ok;
      if (isResNotOk) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? t(i18n)`세션 목록을 불러오지 못했습니다.`);
      }
      const sessions = (await res.json()) as ClientSession[];
      // updatedAt은 wire(JSON)에서 ISO 문자열로 오므로 여기서 epoch ms로 정규화한다.
      // Date 인스턴스를 그대로 캐시에 넣으면 안 된다 — 이 쿼리는 localStorage로
      // persist되고(main.tsx) 직렬화가 JSON이라, 새로고침 후 rehydrate되면 Date가
      // 문자열로 되살아나 소비자(formatTimeAgo)에서 터진다. 숫자는 왕복해도 숫자다.
      // 값이 없거나 파싱 불가면 null로 떨어뜨린다 — new Date(null)/new Date(undefined)의
      // epoch·Invalid Date 조용한 왜곡을 피하고, null은 정렬에서 맨 뒤로 민다.
      return sessions
        .map((s) => {
          const parsed = s.updatedAt ? new Date(s.updatedAt).getTime() : Number.NaN;
          const lastActiveAtMs = Number.isNaN(parsed) ? null : parsed;
          return { ...s, lastActiveAtMs };
        })
        .toSorted((a, b) => (b.lastActiveAtMs ?? 0) - (a.lastActiveAtMs ?? 0));
    },
    // 전역 기본값(1분)보다 오래 캐싱한다 — 서버의 session cookieCache 유효기간(5분,
    // shared/lib/auth.ts 참고)과 맞춰서, 설정 화면을 다시 열 때마다 매번
    // 리페치하지 않게 한다.
    staleTime: 5 * 60 * 1000,
  });
}

/** 현재 세션을 제외한 나머지 모든 세션을 해제한다.
 *  better-auth 의 revokeOtherSessions(KV 추적분만 지움) 대신 커스텀
 *  `/api/sessions/revoke-others` 를 호출해, 목록과 동일하게 DB session 테이블을
 *  정본으로 삼아 비현재 세션을 전부 취소한다 — 유령 세션까지 확실히 제거된다. */
export function useRevokeOtherSessionsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.api.sessions["revoke-others"].$post();
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? t(i18n)`다른 세션을 해제하지 못했습니다.`);
      }
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: sessionKeys.all });
    },
  });
}

/** 세션(기기) 하나를 해제한다. sessionId 는 목록 항목의 session.id 로, 서버가
 *  소유권을 확인한 뒤 token 으로 변환해 취소한다. 성공 시 목록을 무효화해 해제된
 *  기기가 빠진 상태로 리페치한다(revoke-others 는 전량 제거라 removeQueries 를 쓰지만
 *  여기선 한 건만 사라지므로 invalidate 로 최신 목록을 다시 받는다). */
export function useRevokeSessionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const res = await apiClient.api.sessions.revoke.$post({
        json: { sessionId },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? t(i18n)`세션을 해제하지 못했습니다.`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sessionKeys.all });
    },
  });
}
